import React, { useState, useContext } from 'react'
import {
    Box,
    Container,
    VStack,
    Text,
    Heading,
    Button,
    HStack,
    Icon,
    useToast,
    Badge,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Code,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Grid,
    Card,
    CardBody,
    CardHeader,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    List,
    ListItem,
    ListIcon,
    IconButton,
} from '@chakra-ui/react'
import Navbar from '../compnents/Navbar'
import Footer from '../compnents/Footer'
import ThemeContext from '../context/ThemeContext';
import { FaCopy, FaCheck, FaExclamationTriangle, FaShieldAlt, FaBolt, FaLock, FaBook, FaServer } from 'react-icons/fa'

const API_BASE_URL = "https://finansee-api-886082624601.europe-southwest1.run.app";

// API Endpoints organized by category
const API_ENDPOINTS = [
    {
        category: "Authentication",
        endpoints: [
            { method: "POST", path: "/v1/auth/login", description: "Get JWT token" },
            { method: "POST", path: "/v1/auth/register", description: "Register new user (admin only)" },
            { method: "GET", path: "/v1/auth/me", description: "Get current user info" },
        ]
    },
    {
        category: "Customers",
        endpoints: [
            { method: "GET", path: "/v1/customers", description: "List customers with pagination" },
            { method: "GET", path: "/v1/customers/{customer_id}/scores", description: "Get customer score history" },
            { method: "GET", path: "/v1/customers/{customer_id}/bias-report", description: "Get comprehensive bias report" },
        ]
    },
    {
        category: "Scoring",
        endpoints: [
            { method: "POST", path: "/v1/score/batch", description: "Score multiple customers" },
            { method: "GET", path: "/v1/bias-benchmark", description: "Get bias benchmark data" },
        ]
    },
    {
        category: "Dashboards",
        endpoints: [
            { method: "GET", path: "/v1/dashboard/rm", description: "Get RM dashboard" },
            { method: "GET", path: "/v1/dashboard/portfolio-adviser", description: "Get portfolio adviser dashboard" },
            { method: "GET", path: "/v1/dashboard/head", description: "Get head of advisory dashboard" },
            { method: "GET", path: "/v1/dashboard/compliance", description: "Get compliance dashboard" },
            { method: "GET", path: "/v1/dashboard/{dashboard_type}/export.{export_format}", description: "Export dashboard data" },
        ]
    },
    {
        category: "Exports",
        endpoints: [
            { method: "GET", path: "/v1/export/customers.csv", description: "Export customer data as CSV" },
            { method: "GET", path: "/v1/export/bias_scores.csv", description: "Export bias scores as CSV" },
            { method: "GET", path: "/v1/export/customer/{customer_id}/report.pdf", description: "Export customer report as PDF" },
        ]
    },
    {
        category: "Audit & Compliance",
        endpoints: [
            { method: "GET", path: "/v1/audit/logs", description: "Get audit logs" },
            { method: "POST", path: "/v1/audit/export", description: "Export audit logs" },
        ]
    }
];

// Available scopes for API keys
const AVAILABLE_SCOPES = [
    { value: 'read:customers', label: 'Read Customers', description: 'Can read customer data' },
    { value: 'write:customers', label: 'Write Customers', description: 'Can create/update customer data' },
    { value: 'read:scores', label: 'Read Scores', description: 'Can read risk/bias scores' },
    { value: 'write:scores', label: 'Write Scores', description: 'Can write scores' },
    { value: 'read:dashboards', label: 'Read Dashboards', description: 'Can access analytics/dashboard endpoints' },
    { value: 'admin', label: 'Admin', description: 'Full access (like firm admin)' },
    { value: 'read:*', label: 'Read All', description: 'All read permissions' },
    { value: 'write:*', label: 'Write All', description: 'All write permissions' },
];

// Code examples
const CODE_EXAMPLES = {
    python: `import requests

# Using API Key with X-API-Key header
api_key = "your_api_key_here"
headers = {
    "X-API-Key": api_key,
    "Content-Type": "application/json"
}

# Example: Score customers
response = requests.post(
    "https://finansee-api-886082624601.europe-southwest1.run.app/v1/score/batch",
    headers=headers,
    json={
        "customers": [
            {"customer_id": "CUST001"},
            {"customer_id": "CUST002"}
        ],
        "options": {"persist": True}
    }
)

if response.status_code == 200:
    scores = response.json()
    print(f"Scored {len(scores)} customers")
else:
    print(f"Error: {response.status_code}")
    print(response.text)`,

    javascript: `// Using fetch with API Key
const apiKey = 'your_api_key_here';
const baseUrl = 'https://finansee-api-886082624601.europe-southwest1.run.app';

async function scoreCustomers() {
    const response = await fetch(\`\${baseUrl}/v1/score/batch\`, {
        method: 'POST',
        headers: {
            'X-API-Key': apiKey,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            customers: [
                { customer_id: 'CUST001' },
                { customer_id: 'CUST002' }
            ],
            options: { persist: true }
        })
    });

    if (response.ok) {
        const scores = await response.json();
        console.log(\`Scored \${scores.length} customers\`);
    } else {
        console.error('Error:', response.status, await response.text());
    }
}

// Get customers
async function getCustomers() {
    const response = await fetch(\`\${baseUrl}/v1/customers?page=1&page_size=25\`, {
        headers: {
            'X-API-Key': apiKey
        }
    });
    
    if (response.ok) {
        const data = await response.json();
        return data.customers;
    }
}`,

    curl: `# Authentication with API Key using X-API-Key header
curl -X POST "https://finansee-api-886082624601.europe-southwest1.run.app/v1/score/batch" \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "customers": [
      {"customer_id": "CUST001"},
      {"customer_id": "CUST002"}
    ],
    "options": {"persist": true}
  }'

# Get customers
curl -X GET "https://finansee-api-886082624601.europe-southwest1.run.app/v1/customers?page=1&page_size=25" \\
  -H "X-API-Key: your_api_key_here"

# Get customer scores
curl -X GET "https://finansee-api-886082624601.europe-southwest1.run.app/v1/customers/CUST001/scores?latest=true" \\
  -H "X-API-Key: your_api_key_here"`,

    errorHandling: `# Common error responses
# 401 - Unauthorized (invalid or missing API key)
{
    "detail": "Invalid API key"
}

# 403 - Forbidden (insufficient scopes)
{
    "detail": "Insufficient permissions for this resource"
}

# 429 - Rate limited
{
    "detail": "Rate limit exceeded. Please try again later.",
    "retry_after": 60
}

# 422 - Validation error
{
    "detail": [
        {
            "loc": ["body", "customer_id"],
            "msg": "field required",
            "type": "value_error.missing"
        }
    ]
}`
};

function Docs() {
    const { themeData } = useContext(ThemeContext);
    const toast = useToast();
    // eslint-disable-next-line
    const [activeTab, setActiveTab] = useState(0);
    const [activeCodeExample, setActiveCodeExample] = useState('python');

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const backgroundColor = themeData?.colors?.background || '#F7FAFC';

    // Determine if theme is dark
    const isDarkTheme = themeData?.id === 'midnight';

    // Text colors
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.600';
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const headingTextColor = isDarkTheme ? '#F7FAFC' : 'gray.700';
    const strongTextColor = isDarkTheme ? '#F7FAFC' : 'gray.800';
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';
    const codeBg = isDarkTheme ? 'rgba(26, 32, 44, 0.8)' : 'gray.50';
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const successColor = isDarkTheme ? '#68D391' : '#38A169';
    const errorColor = isDarkTheme ? '#FC8181' : '#E53E3E';

    // Copy code to clipboard
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast({
            title: 'Copied to clipboard',
            status: 'success',
            duration: 2000,
            isClosable: true,
        });
    };

    return (
        <Box minH="100vh" display="flex" flexDirection="column">
            <Navbar />
            <Box display="flex" flex="1" bg={backgroundColor}>
                <Container maxW="container.xl" flex="1" py={8} px={{ base: 4, md: 6 }}>
                    <VStack spacing={8} align="stretch">
                        {/* Header */}
                        <Box>
                            <HStack spacing={4} mb={3}>
                                <Icon as={FaBook} boxSize={8} color={primaryColor} />
                                <Heading as="h1" size="xl" color={headingTextColor} fontWeight="bold">
                                    API Documentation
                                </Heading>
                            </HStack>
                            <Text fontSize="lg" color={bodyTextColor}>
                                Comprehensive guide for integrating with FinanSee's behavioral bias detection platform using API keys
                            </Text>
                            <Box mt={4} p={4} bg={codeBg} borderRadius="md" border="1px" borderColor={borderColor}>
                                <HStack spacing={2} mb={2}>
                                    <Text fontWeight="semibold" color={headingTextColor} fontSize="sm">
                                        Base URL:
                                    </Text>
                                </HStack>
                                <Code color={strongTextColor} fontSize="md" fontFamily="mono">
                                    {API_BASE_URL}
                                </Code>
                            </Box>
                        </Box>

                        {/* Main Content Tabs */}
                        <Tabs colorScheme="blue" variant="enclosed" onChange={(index) => setActiveTab(index)}>
                            <TabList>
                                <Tab fontWeight="semibold">Overview</Tab>
                                <Tab fontWeight="semibold">Authentication</Tab>
                                <Tab fontWeight="semibold">Endpoints</Tab>
                                <Tab fontWeight="semibold">Code Examples</Tab>
                                <Tab fontWeight="semibold">Best Practices</Tab>
                            </TabList>

                            <TabPanels mt={6}>
                                {/* Overview Tab */}
                                <TabPanel px={0}>
                                    <VStack spacing={8} align="stretch">
                                        {/* Introduction */}
                                        <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                            <CardHeader pb={2}>
                                                <Heading size="md" color={headingTextColor}>
                                                    Introduction to API Keys
                                                </Heading>
                                            </CardHeader>
                                            <CardBody>
                                                <VStack spacing={4} align="stretch">
                                                    <Text color={bodyTextColor}>
                                                        FinanSee's API keys provide programmatic access to the behavioral bias detection platform,
                                                        enabling automation, integration with internal systems, and custom analytics workflows.
                                                    </Text>
                                                    <HStack spacing={3}>
                                                        <Badge colorScheme="green" variant="subtle">Enterprise Feature</Badge>
                                                        <Badge colorScheme="blue" variant="subtle">Business Subscription</Badge>
                                                        <Badge colorScheme="purple" variant="subtle">REST API</Badge>
                                                    </HStack>
                                                </VStack>
                                            </CardBody>
                                        </Card>

                                        {/* Key Features */}
                                        <Box>
                                            <Heading size="lg" color={headingTextColor} mb={4}>
                                                Key Features
                                            </Heading>
                                            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
                                                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                                    <CardBody>
                                                        <HStack spacing={3} mb={3}>
                                                            <Icon as={FaLock} color={primaryColor} />
                                                            <Heading size="sm" color={headingTextColor}>Scope-Based Permissions</Heading>
                                                        </HStack>
                                                        <Text color={bodyTextColor} fontSize="sm">
                                                            Granular access control with specific scopes for different operations.
                                                            Create keys with only the permissions needed.
                                                        </Text>
                                                    </CardBody>
                                                </Card>

                                                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                                    <CardBody>
                                                        <HStack spacing={3} mb={3}>
                                                            <Icon as={FaBolt} color={primaryColor} />
                                                            <Heading size="sm" color={headingTextColor}>Rate Limiting</Heading>
                                                        </HStack>
                                                        <Text color={bodyTextColor} fontSize="sm">
                                                            Configurable rate limits (per minute, hour, day) to protect system resources.
                                                            Default: 60/min, 1000/hour, 10000/day.
                                                        </Text>
                                                    </CardBody>
                                                </Card>

                                                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                                    <CardBody>
                                                        <HStack spacing={3} mb={3}>
                                                            <Icon as={FaShieldAlt} color={primaryColor} />
                                                            <Heading size="sm" color={headingTextColor}>Tenant Isolation</Heading>
                                                        </HStack>
                                                        <Text color={bodyTextColor} fontSize="sm">
                                                            API keys are fully isolated per tenant. No cross-tenant data access.
                                                            Each key can only access its own tenant's data.
                                                        </Text>
                                                    </CardBody>
                                                </Card>

                                                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                                    <CardBody>
                                                        <HStack spacing={3} mb={3}>
                                                            <Icon as={FaServer} color={primaryColor} />
                                                            <Heading size="sm" color={headingTextColor}>Usage Monitoring</Heading>
                                                        </HStack>
                                                        <Text color={bodyTextColor} fontSize="sm">
                                                            Track usage counts, last used timestamps, and audit all API calls.
                                                            Full visibility into API key activity.
                                                        </Text>
                                                    </CardBody>
                                                </Card>
                                            </Grid>
                                        </Box>

                                        {/* What API Keys Can and Cannot Do */}
                                        <Box>
                                            <Heading size="lg" color={headingTextColor} mb={4}>
                                                Capabilities & Limitations
                                            </Heading>
                                            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
                                                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                                    <CardHeader pb={2}>
                                                        <Heading size="md" color={successColor}>
                                                            ✅ Can Do
                                                        </Heading>
                                                    </CardHeader>
                                                    <CardBody>
                                                        <List spacing={3}>
                                                            <ListItem color={bodyTextColor}>
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Call the same B2B APIs your web app uses
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor}>
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Read/write data based on assigned scopes
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor}>
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Access dashboards, scores, and customer data
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor}>
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Export data in CSV, PNG, and PDF formats
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor}>
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Submit batch scoring requests
                                                            </ListItem>
                                                        </List>
                                                    </CardBody>
                                                </Card>

                                                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                                    <CardHeader pb={2}>
                                                        <Heading size="md" color={errorColor}>
                                                            ❌ Cannot Do
                                                        </Heading>
                                                    </CardHeader>
                                                    <CardBody>
                                                        <List spacing={3}>
                                                            <ListItem color={bodyTextColor}>
                                                                <ListIcon as={FaExclamationTriangle} color={errorColor} />
                                                                Manage users (create/delete users, change roles)
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor}>
                                                                <ListIcon as={FaExclamationTriangle} color={errorColor} />
                                                                Manage tenants or subscription plans
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor}>
                                                                <ListIcon as={FaExclamationTriangle} color={errorColor} />
                                                                Manage API keys themselves (admin UI only)
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor}>
                                                                <ListIcon as={FaExclamationTriangle} color={errorColor} />
                                                                Access system-level/admin-only operations
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor}>
                                                                <ListIcon as={FaExclamationTriangle} color={errorColor} />
                                                                Cross-tenant data access
                                                            </ListItem>
                                                        </List>
                                                    </CardBody>
                                                </Card>
                                            </Grid>
                                        </Box>

                                    </VStack>
                                </TabPanel>

                                {/* Authentication Tab */}
                                <TabPanel px={0}>
                                    <VStack spacing={8} align="stretch">
                                        {/* Authentication Methods */}
                                        <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                            <CardHeader pb={2}>
                                                <Heading size="md" color={headingTextColor}>
                                                    Authentication Method
                                                </Heading>
                                            </CardHeader>
                                            <CardBody>
                                                <VStack spacing={6} align="stretch">
                                                    <Box>
                                                        <Heading size="sm" color={headingTextColor} mb={3}>
                                                            Using X-API-Key Header
                                                        </Heading>
                                                        <Text color={bodyTextColor} fontSize="sm" mb={3}>
                                                            Include your API key in the X-API-Key header for all API requests.
                                                        </Text>
                                                        <Box bg={codeBg} p={4} borderRadius="md" position="relative">
                                                            <Code color={strongTextColor} fontSize="sm" fontFamily="mono">
                                                                X-API-Key: your_api_key_here
                                                            </Code>
                                                            <IconButton
                                                                icon={<FaCopy />}
                                                                size="sm"
                                                                position="absolute"
                                                                top={2}
                                                                right={2}
                                                                onClick={() => copyToClipboard('X-API-Key: your_api_key_here')}
                                                                aria-label="Copy code"
                                                            />
                                                        </Box>
                                                    </Box>

                                                    <Alert status="warning" borderRadius="md">
                                                        <AlertIcon />
                                                        <Box>
                                                            <AlertTitle fontSize="sm">Important Security Note</AlertTitle>
                                                            <AlertDescription fontSize="xs">
                                                                The API key is shown only once when created. Store it securely (environment variables, secret manager).
                                                                Never commit API keys to version control.
                                                            </AlertDescription>
                                                        </Box>
                                                    </Alert>
                                                </VStack>
                                            </CardBody>
                                        </Card>

                                        {/* Available Scopes */}
                                        <Box>
                                            <Heading size="lg" color={headingTextColor} mb={4}>
                                                Available Scopes
                                            </Heading>
                                            <Text color={bodyTextColor} mb={4}>
                                                Scopes define what actions an API key can perform. Choose only the permissions needed for each use case.
                                            </Text>
                                            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                                                {AVAILABLE_SCOPES.map((scope) => (
                                                    <Card key={scope.value} bg={cardBg} border="1px" borderColor={borderColor} size="sm">
                                                        <CardBody p={4}>
                                                            <VStack align="start" spacing={2}>
                                                                <HStack>
                                                                    <Badge colorScheme="blue">{scope.value}</Badge>
                                                                    <Text fontWeight="semibold" color={headingTextColor} fontSize="sm">
                                                                        {scope.label}
                                                                    </Text>
                                                                </HStack>
                                                                <Text color={mutedTextColor} fontSize="xs">
                                                                    {scope.description}
                                                                </Text>
                                                            </VStack>
                                                        </CardBody>
                                                    </Card>
                                                ))}
                                            </Grid>
                                        </Box>

                                        {/* Error Responses */}
                                        <Box>
                                            <Heading size="lg" color={headingTextColor} mb={4}>
                                                Error Handling
                                            </Heading>
                                            <VStack spacing={4} align="stretch">
                                                <HStack spacing={4}>
                                                    <Badge colorScheme="red" fontSize="md" px={3} py={1}>401</Badge>
                                                    <Box flex="1">
                                                        <Text fontWeight="semibold" color={headingTextColor}>
                                                            Invalid or missing API key
                                                        </Text>
                                                        <Text color={bodyTextColor} fontSize="sm">
                                                            Check that your API key is correct and included in the X-API-Key header
                                                        </Text>
                                                    </Box>
                                                </HStack>

                                                <HStack spacing={4}>
                                                    <Badge colorScheme="orange" fontSize="md" px={3} py={1}>403</Badge>
                                                    <Box flex="1">
                                                        <Text fontWeight="semibold" color={headingTextColor}>
                                                            Insufficient scopes
                                                        </Text>
                                                        <Text color={bodyTextColor} fontSize="sm">
                                                            Your API key doesn't have the required permissions for this endpoint
                                                        </Text>
                                                    </Box>
                                                </HStack>

                                                <HStack spacing={4}>
                                                    <Badge colorScheme="yellow" fontSize="md" px={3} py={1}>429</Badge>
                                                    <Box flex="1">
                                                        <Text fontWeight="semibold" color={headingTextColor}>
                                                            Rate limit exceeded
                                                        </Text>
                                                        <Text color={bodyTextColor} fontSize="sm">
                                                            You've exceeded your API key's rate limits. Check your key's configuration
                                                        </Text>
                                                    </Box>
                                                </HStack>

                                                <HStack spacing={4}>
                                                    <Badge colorScheme="purple" fontSize="md" px={3} py={1}>422</Badge>
                                                    <Box flex="1">
                                                        <Text fontWeight="semibold" color={headingTextColor}>
                                                            Validation error
                                                        </Text>
                                                        <Text color={bodyTextColor} fontSize="sm">
                                                            Request data is invalid or missing required fields
                                                        </Text>
                                                    </Box>
                                                </HStack>
                                            </VStack>
                                        </Box>
                                    </VStack>
                                </TabPanel>

                                {/* Endpoints Tab */}
                                <TabPanel px={0}>
                                    <VStack spacing={6} align="stretch">
                                        <Box>
                                            <Heading size="lg" color={headingTextColor} mb={4}>
                                                API Endpoints Reference
                                            </Heading>
                                            <Text color={bodyTextColor} mb={6}>
                                                All endpoints support API key authentication via the X-API-Key header.
                                            </Text>

                                            {API_ENDPOINTS.map((category) => (
                                                <Box key={category.category} mb={8}>
                                                    <Heading size="md" color={headingTextColor} mb={4} pb={2} borderBottom="1px" borderColor={borderColor}>
                                                        {category.category}
                                                    </Heading>
                                                    <Box overflowX="auto">
                                                        <Table variant="simple">
                                                            <Thead bg={isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : 'gray.50'}>
                                                                <Tr>
                                                                    <Th color={bodyTextColor} borderColor={borderColor}>Method</Th>
                                                                    <Th color={bodyTextColor} borderColor={borderColor}>Endpoint</Th>
                                                                    <Th color={bodyTextColor} borderColor={borderColor}>Description</Th>
                                                                </Tr>
                                                            </Thead>
                                                            <Tbody>
                                                                {category.endpoints.map((endpoint, idx) => (
                                                                    <Tr key={idx}>
                                                                        <Td borderColor={borderColor}>
                                                                            <Badge
                                                                                colorScheme={
                                                                                    endpoint.method === 'GET' ? 'green' :
                                                                                        endpoint.method === 'POST' ? 'blue' :
                                                                                            endpoint.method === 'PUT' ? 'yellow' :
                                                                                                endpoint.method === 'DELETE' ? 'red' : 'gray'
                                                                                }
                                                                                fontSize="xs"
                                                                            >
                                                                                {endpoint.method}
                                                                            </Badge>
                                                                        </Td>
                                                                        <Td borderColor={borderColor}>
                                                                            <Code color={strongTextColor} fontSize="sm" fontFamily="mono">
                                                                                {endpoint.path}
                                                                            </Code>
                                                                        </Td>
                                                                        <Td borderColor={borderColor} color={bodyTextColor}>
                                                                            {endpoint.description}
                                                                        </Td>
                                                                    </Tr>
                                                                ))}
                                                            </Tbody>
                                                        </Table>
                                                    </Box>
                                                </Box>
                                            ))}

                                        </Box>
                                    </VStack>
                                </TabPanel>

                                {/* Code Examples Tab */}
                                <TabPanel px={0}>
                                    <VStack spacing={8} align="stretch">
                                        <Box>
                                            <Heading size="lg" color={headingTextColor} mb={4}>
                                                Code Examples
                                            </Heading>
                                            <Text color={bodyTextColor} mb={6}>
                                                Examples for integrating with FinanSee API in different programming languages.
                                            </Text>

                                            {/* Language Selector */}
                                            <HStack spacing={4} mb={6} flexWrap="wrap">
                                                <Button
                                                    size="sm"
                                                    colorScheme={activeCodeExample === 'python' ? 'blue' : 'gray'}
                                                    variant={activeCodeExample === 'python' ? 'solid' : 'outline'}
                                                    onClick={() => setActiveCodeExample('python')}
                                                >
                                                    Python
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    colorScheme={activeCodeExample === 'javascript' ? 'blue' : 'gray'}
                                                    variant={activeCodeExample === 'javascript' ? 'solid' : 'outline'}
                                                    onClick={() => setActiveCodeExample('javascript')}
                                                >
                                                    JavaScript/Node.js
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    colorScheme={activeCodeExample === 'curl' ? 'blue' : 'gray'}
                                                    variant={activeCodeExample === 'curl' ? 'solid' : 'outline'}
                                                    onClick={() => setActiveCodeExample('curl')}
                                                >
                                                    cURL
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    colorScheme={activeCodeExample === 'errorHandling' ? 'blue' : 'gray'}
                                                    variant={activeCodeExample === 'errorHandling' ? 'solid' : 'outline'}
                                                    onClick={() => setActiveCodeExample('errorHandling')}
                                                >
                                                    Error Handling
                                                </Button>
                                            </HStack>

                                            {/* Code Display */}
                                            <Box position="relative">
                                                <Box
                                                    bg={isDarkTheme ? '#1a202c' : 'gray.800'}
                                                    p={4}
                                                    borderRadius="md"
                                                    overflow="auto"
                                                >
                                                    <pre style={{ margin: 0 }}>
                                                        <code style={{
                                                            color: isDarkTheme ? '#e2e8f0' : '#f7fafc',
                                                            fontFamily: 'monospace',
                                                            fontSize: '14px',
                                                            lineHeight: '1.5'
                                                        }}>
                                                            {CODE_EXAMPLES[activeCodeExample]}
                                                        </code>
                                                    </pre>
                                                </Box>
                                                <IconButton
                                                    icon={<FaCopy />}
                                                    size="sm"
                                                    position="absolute"
                                                    top={3}
                                                    right={3}
                                                    onClick={() => copyToClipboard(CODE_EXAMPLES[activeCodeExample])}
                                                    aria-label="Copy code"
                                                    bg={isDarkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
                                                    _hover={{ bg: isDarkTheme ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)' }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* Common Use Cases */}
                                        <Box>
                                            <Heading size="lg" color={headingTextColor} mb={4}>
                                                Common Use Cases
                                            </Heading>
                                            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
                                                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                                    <CardBody>
                                                        <Heading size="sm" color={headingTextColor} mb={3}>
                                                            Automated Scoring Pipeline
                                                        </Heading>
                                                        <Text color={bodyTextColor} fontSize="sm" mb={3}>
                                                            Integrate bias scoring into your customer onboarding or periodic review processes.
                                                        </Text>
                                                        <Badge colorScheme="purple" variant="subtle" fontSize="xs">/v1/score/batch</Badge>
                                                    </CardBody>
                                                </Card>

                                                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                                    <CardBody>
                                                        <Heading size="sm" color={headingTextColor} mb={3}>
                                                            Dashboard Integration
                                                        </Heading>
                                                        <Text color={bodyTextColor} fontSize="sm" mb={3}>
                                                            Embed bias metrics into your internal dashboards or reporting tools.
                                                        </Text>
                                                        <Badge colorScheme="purple" variant="subtle" fontSize="xs">/v1/dashboard/*</Badge>
                                                    </CardBody>
                                                </Card>

                                                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                                    <CardBody>
                                                        <Heading size="sm" color={headingTextColor} mb={3}>
                                                            Data Export & Reporting
                                                        </Heading>
                                                        <Text color={bodyTextColor} fontSize="sm" mb={3}>
                                                            Automate export of bias scores for compliance reporting or analytics.
                                                        </Text>
                                                        <Badge colorScheme="purple" variant="subtle" fontSize="xs">/v1/export/*</Badge>
                                                    </CardBody>
                                                </Card>

                                                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                                    <CardBody>
                                                        <Heading size="sm" color={headingTextColor} mb={3}>
                                                            Customer Prioritization
                                                        </Heading>
                                                        <Text color={bodyTextColor} fontSize="sm" mb={3}>
                                                            Build custom prioritization systems using bias scores and risk metrics.
                                                        </Text>
                                                        <Badge colorScheme="purple" variant="subtle" fontSize="xs">/v1/rm/who-to-call</Badge>
                                                    </CardBody>
                                                </Card>
                                            </Grid>
                                        </Box>
                                    </VStack>
                                </TabPanel>

                                {/* Best Practices Tab */}
                                <TabPanel px={0}>
                                    <VStack spacing={8} align="stretch">
                                        <Box>
                                            <Heading size="lg" color={headingTextColor} mb={4}>
                                                Security Best Practices
                                            </Heading>
                                            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
                                                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                                    <CardBody>
                                                        <HStack spacing={3} mb={3}>
                                                            <Icon as={FaShieldAlt} color={successColor} />
                                                            <Heading size="sm" color={headingTextColor}>Store Keys Securely</Heading>
                                                        </HStack>
                                                        <List spacing={2}>
                                                            <ListItem color={bodyTextColor} fontSize="sm">
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Use environment variables or secret managers
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor} fontSize="sm">
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Never commit API keys to version control
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor} fontSize="sm">
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Rotate keys periodically (every 90-180 days)
                                                            </ListItem>
                                                        </List>
                                                    </CardBody>
                                                </Card>

                                                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                                    <CardBody>
                                                        <HStack spacing={3} mb={3}>
                                                            <Icon as={FaLock} color={successColor} />
                                                            <Heading size="sm" color={headingTextColor}>Principle of Least Privilege</Heading>
                                                        </HStack>
                                                        <List spacing={2}>
                                                            <ListItem color={bodyTextColor} fontSize="sm">
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Create separate keys for different applications
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor} fontSize="sm">
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Assign only necessary scopes to each key
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor} fontSize="sm">
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Use read-only keys for monitoring applications
                                                            </ListItem>
                                                        </List>
                                                    </CardBody>
                                                </Card>

                                                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                                    <CardBody>
                                                        <HStack spacing={3} mb={3}>
                                                            <Icon as={FaServer} color={successColor} />
                                                            <Heading size="sm" color={headingTextColor}>Monitoring & Auditing</Heading>
                                                        </HStack>
                                                        <List spacing={2}>
                                                            <ListItem color={bodyTextColor} fontSize="sm">
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Monitor usage patterns for anomalies
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor} fontSize="sm">
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Set up alerts for rate limit breaches
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor} fontSize="sm">
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Review audit logs regularly
                                                            </ListItem>
                                                        </List>
                                                    </CardBody>
                                                </Card>

                                                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                                                    <CardBody>
                                                        <HStack spacing={3} mb={3}>
                                                            <Icon as={FaBolt} color={successColor} />
                                                            <Heading size="sm" color={headingTextColor}>Performance & Reliability</Heading>
                                                        </HStack>
                                                        <List spacing={2}>
                                                            <ListItem color={bodyTextColor} fontSize="sm">
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Implement exponential backoff for retries
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor} fontSize="sm">
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Cache responses where appropriate
                                                            </ListItem>
                                                            <ListItem color={bodyTextColor} fontSize="sm">
                                                                <ListIcon as={FaCheck} color={successColor} />
                                                                Set appropriate timeouts for requests
                                                            </ListItem>
                                                        </List>
                                                    </CardBody>
                                                </Card>
                                            </Grid>
                                        </Box>

                                        {/* FAQ Accordion */}
                                        <Box>
                                            <Heading size="lg" color={headingTextColor} mb={4}>
                                                Frequently Asked Questions
                                            </Heading>
                                            <Accordion allowMultiple>
                                                <AccordionItem border="none" mb={3}>
                                                    <AccordionButton
                                                        bg={cardBg}
                                                        borderRadius="md"
                                                        border="1px"
                                                        borderColor={borderColor}
                                                        _hover={{ bg: isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : 'gray.50' }}
                                                    >
                                                        <Box flex="1" textAlign="left">
                                                            <Heading size="sm" color={headingTextColor}>
                                                                How are API keys different from user authentication?
                                                            </Heading>
                                                        </Box>
                                                        <AccordionIcon />
                                                    </AccordionButton>
                                                    <AccordionPanel pb={4} color={bodyTextColor}>
                                                        API keys are for programmatic access by machines and services, while user authentication (JWT) is for human users via the web interface. API keys have configurable scopes and rate limits, don't expire unless set to, and can't perform user management operations.
                                                    </AccordionPanel>
                                                </AccordionItem>

                                                <AccordionItem border="none" mb={3}>
                                                    <AccordionButton
                                                        bg={cardBg}
                                                        borderRadius="md"
                                                        border="1px"
                                                        borderColor={borderColor}
                                                        _hover={{ bg: isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : 'gray.50' }}
                                                    >
                                                        <Box flex="1" textAlign="left">
                                                            <Heading size="sm" color={headingTextColor}>
                                                                What happens if I lose an API key?
                                                            </Heading>
                                                        </Box>
                                                        <AccordionIcon />
                                                    </AccordionButton>
                                                    <AccordionPanel pb={4} color={bodyTextColor}>
                                                        API keys are only shown once during creation. If you lose a key, you must revoke it and create a new one. There's no way to retrieve an existing key's value for security reasons.
                                                    </AccordionPanel>
                                                </AccordionItem>

                                                <AccordionItem border="none" mb={3}>
                                                    <AccordionButton
                                                        bg={cardBg}
                                                        borderRadius="md"
                                                        border="1px"
                                                        borderColor={borderColor}
                                                        _hover={{ bg: isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : 'gray.50' }}
                                                    >
                                                        <Box flex="1" textAlign="left">
                                                            <Heading size="sm" color={headingTextColor}>
                                                                Can I use API keys for cross-tenant access?
                                                            </Heading>
                                                        </Box>
                                                        <AccordionIcon />
                                                    </AccordionButton>
                                                    <AccordionPanel pb={4} color={bodyTextColor}>
                                                        No. API keys are strictly isolated per tenant. Each key can only access data from its own tenant. This is a core security feature of the multi-tenant architecture.
                                                    </AccordionPanel>
                                                </AccordionItem>

                                                <AccordionItem border="none" mb={3}>
                                                    <AccordionButton
                                                        bg={cardBg}
                                                        borderRadius="md"
                                                        border="1px"
                                                        borderColor={borderColor}
                                                        _hover={{ bg: isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : 'gray.50' }}
                                                    >
                                                        <Box flex="1" textAlign="left">
                                                            <Heading size="sm" color={headingTextColor}>
                                                                How do I handle rate limiting in my application?
                                                            </Heading>
                                                        </Box>
                                                        <AccordionIcon />
                                                    </AccordionButton>
                                                    <AccordionPanel pb={4} color={bodyTextColor}>
                                                        Implement exponential backoff with jitter. When you receive a 429 response, check the Retry-After header for how long to wait. Consider implementing request queuing or caching for high-volume applications.
                                                    </AccordionPanel>
                                                </AccordionItem>
                                            </Accordion>
                                        </Box>

                                        {/* Support Section */}
                                        <Alert status="info" borderRadius="md">
                                            <AlertIcon />
                                            <Box flex="1">
                                                <AlertTitle fontSize="sm">Need Additional Help?</AlertTitle>
                                                <AlertDescription fontSize="xs">
                                                    For API support, contact your FinanSee administrator.
                                                </AlertDescription>
                                            </Box>
                                        </Alert>
                                    </VStack>
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </VStack>
                </Container>
            </Box>
            <Footer />
        </Box>
    )
}

export default Docs